import {
  DailyRecord,
  AggregatedData,
  EffortMetrics,
  ProductionMetrics,
} from '../types/salesMetrics'
import {
  startOfDay,
  subDays,
  isSameDay,
  parseISO,
  isWithinInterval,
  subWeeks,
} from 'date-fns'

// Helper to sum effort metrics
export const sumEffort = (records: DailyRecord[]): EffortMetrics => {
  return records.reduce(
    (acc, record) => ({
      inboundCalls: acc.inboundCalls + record.effort.inboundCalls,
      inboundTalkTime: acc.inboundTalkTime + record.effort.inboundTalkTime,
      rehashCalls: acc.rehashCalls + record.effort.rehashCalls,
      rehashTalkTime: acc.rehashTalkTime + record.effort.rehashTalkTime,
      outboundCalls: acc.outboundCalls + record.effort.outboundCalls,
      outboundTalkTime: acc.outboundTalkTime + record.effort.outboundTalkTime,
      personalLineCalls:
        acc.personalLineCalls + record.effort.personalLineCalls,
      personalLineTalkTime:
        acc.personalLineTalkTime + record.effort.personalLineTalkTime,
      smsSent: acc.smsSent + record.effort.smsSent,
      callsRejected: acc.callsRejected + record.effort.callsRejected,
      missedCalls: acc.missedCalls + record.effort.missedCalls,
      declinedTransfers:
        acc.declinedTransfers + record.effort.declinedTransfers,
    }),
    {
      inboundCalls: 0,
      inboundTalkTime: 0,
      rehashCalls: 0,
      rehashTalkTime: 0,
      outboundCalls: 0,
      outboundTalkTime: 0,
      personalLineCalls: 0,
      personalLineTalkTime: 0,
      smsSent: 0,
      callsRejected: 0,
      missedCalls: 0,
      declinedTransfers: 0,
    },
  )
}

// Helper to sum production metrics
export const sumProduction = (records: DailyRecord[]): ProductionMetrics => {
  return records.reduce(
    (acc, record) => ({
      applications: acc.applications + record.production.applications,
      cbasSent: acc.cbasSent + record.production.cbasSent,
      locks: acc.locks + record.production.locks,
      stps: acc.stps + record.production.stps,
    }),
    {
      applications: 0,
      cbasSent: 0,
      locks: 0,
      stps: 0,
    },
  )
}

export const aggregateData = (records: DailyRecord[]): AggregatedData => {
  return {
    effort: sumEffort(records),
    production: sumProduction(records),
    recordCount: records.length,
  }
}

export const filterRecordsByDate = (
  records: DailyRecord[],
  range: string,
  customStart?: Date,
  customEnd?: Date,
): DailyRecord[] => {
  const today = startOfDay(new Date())

  return records.filter((record) => {
    const recordDate = parseISO(record.date)

    switch (range) {
      case 'today':
        return isSameDay(recordDate, today)
      case 'yesterday':
        return isSameDay(recordDate, subDays(today, 1))
      case '7days':
        return isWithinInterval(recordDate, {
          start: subDays(today, 6),
          end: today,
        })
      case '30days':
        return isWithinInterval(recordDate, {
          start: subDays(today, 29),
          end: today,
        })
      case 'custom':
        if (customStart && customEnd) {
          return isWithinInterval(recordDate, {
            start: customStart,
            end: customEnd,
          })
        }
        return true
      default:
        return true
    }
  })
}

export const calculateTrend = (current: number, previous: number) => {
  if (previous === 0)
    return { value: current, percentageChange: 100, direction: 'up' as const }

  const diff = current - previous
  const percentage = (diff / previous) * 100

  return {
    value: current,
    percentageChange: Math.abs(percentage),
    direction:
      percentage > 0 ? 'up' : percentage < 0 ? 'down' : ('flat' as const),
  }
}

export const getTotalCalls = (effort: EffortMetrics) => {
  return (
    effort.inboundCalls +
    effort.rehashCalls +
    effort.outboundCalls +
    effort.personalLineCalls
  )
}

export const getTotalTalkTime = (effort: EffortMetrics) => {
  return (
    effort.inboundTalkTime +
    effort.rehashTalkTime +
    effort.outboundTalkTime +
    effort.personalLineTalkTime
  )
}
